import { useEffect, useState } from "react";
import { api, formatDate } from "../lib/api";
import { PageHeader, Empty } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Textarea } from "../components/ui/textarea";
import { Check, X, Clock } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["Applied", "Legal Review", "Finance Approval", "Active"];

export default function Onboarding() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [comment, setComment] = useState("");

  const loadItems = async () => {
    try {
      const { data } = await api.get("/onboarding");
      setItems(data || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => { loadItems(); }, []);

  const move = async (oid, stage) => {
    try { await api.put(`/onboarding/${oid}/move`, { stage }); toast.success(`Moved to ${stage}`); loadItems(); setOpen(null); }
    catch { toast.error("Could not update"); }
  };

  const addComment = async () => {
    if (!comment.trim() || !open) return;
    try {
      await api.post(`/onboarding/${open.id}/comments`, { text: comment });
      setComment("");
      const { data } = await api.get("/onboarding");
      setItems(data);
      setOpen(data.find((x) => x.id === open.id));
    } catch { toast.error("Could not add comment"); }
  };

  const byStage = (stage) => (items || []).filter((i) => i.stage === stage);

  return (
    <div className="animate-fade-in">
      <PageHeader title="Onboarding Workflow" subtitle="Track vendors through your approval pipeline" />

      {items === null && <Skeleton className="h-96" />}
      {items && items.length === 0 && <Empty title="No applications yet" subtitle="New vendor applications will show up here" />}

      {items && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {STAGES.map((stage) => (
            <div key={stage} className="bg-white border border-zinc-200 rounded-md" data-testid={`stage-${stage.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                <h3 className="font-semibold text-sm">{stage}</h3>
                <span className="text-xs bg-zinc-100 px-2 py-0.5 rounded-full">{byStage(stage).length}</span>
              </div>
              <div className="p-3 space-y-2 min-h-[200px]">
                {byStage(stage).map((it) => (
                  <button type="button" key={it.id} onClick={() => { const item = it; setTimeout(() => setOpen(item), 0); }} className="w-full text-left bg-zinc-50 hover:bg-white hover:border-[#6366F1] border border-zinc-200 rounded-md p-3 transition-colors" data-testid="onboarding-card">
                    <div className="font-semibold text-sm">{it.vendor_name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{it.category}</div>
                    <Progress value={it.progress} className="h-1 mt-2" />
                    <div className="flex items-center justify-between mt-2 text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Reviewer: {it.assigned_reviewer}</span>
                    </div>
                  </button>
                ))}
                {byStage(stage).length === 0 && (
                  <div className="text-center text-xs text-zinc-400 py-8">No vendors</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {open && (
            <>
              <SheetHeader>
                <SheetTitle>{open.vendor_name}</SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Stage</div>
                  <div className="font-semibold">{open.stage}</div>
                  <Progress value={open.progress} className="h-2 mt-2" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Document Checklist</div>
                  <div className="space-y-1">
                    {(open.document_checklist || []).map((d, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                        <span>{d.name}</span>
                        {d.status === "submitted" ? (
                          <span className="text-emerald-600 text-xs flex items-center gap-1"><Check className="w-3 h-3" /> Submitted</span>
                        ) : (
                          <span className="text-amber-600 text-xs">Pending</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Comments</div>
                  <div className="space-y-2">
                    {(open.comments || []).map((c) => (
                      <div key={c.id} className="bg-zinc-50 rounded-md p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold">{c.author}</span>
                          <span className="text-xs text-zinc-500">{formatDate(c.created_at)}</span>
                        </div>
                        <p className="text-sm text-zinc-700 mt-1">{c.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." rows={2} data-testid="onboarding-comment-input" />
                    <Button size="sm" className="mt-2 bg-[#6366F1] hover:bg-[#4F46E5]" onClick={addComment} data-testid="onboarding-comment-submit">Post comment</Button>
                  </div>
                </div>
                <div className="flex gap-2 pt-4 border-t">
                  {STAGES.indexOf(open.stage) < STAGES.length - 1 && (
                    <Button onClick={() => move(open.id, STAGES[STAGES.indexOf(open.stage) + 1])} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="onboarding-approve"><Check className="w-4 h-4 mr-1.5" /> Approve & advance</Button>
                  )}
                  <Button variant="outline" onClick={() => move(open.id, "Applied")} className="flex-1" data-testid="onboarding-reject"><X className="w-4 h-4 mr-1.5" /> Reset</Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
